import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { referencesApiService } from '../../services/api';
import { Tariff } from '../../types/api';
import { useLanguage } from '../../contexts/LanguageContext';

const SUPPORT_PHONE = '+77772468483';
const SUPPORT_PHONE_DISPLAY = '+7 777 246 84 83';
const SUPPORT_EMAIL = 'partner@qprice.kz';
const SUPPORT_HOURS = 'Пн–Пт · 9:00 – 20:00';

/** Карточка тарифа для отрисовки: справочные поля, приведённые к тексту. */
interface TariffCard {
  id: number;
  name: string;
  duration: string;
  price: string;
  pricePerMonth?: string;
  recommended: boolean;
  trial: boolean;
}

const pluralDays = (n: number): string => {
  const mod100 = n % 100;
  const mod10 = n % 10;
  if (mod100 >= 11 && mod100 <= 14) return `${n} дней`;
  if (mod10 === 1) return `${n} день`;
  if (mod10 >= 2 && mod10 <= 4) return `${n} дня`;
  return `${n} дней`;
};

const formatPrice = (value: number): string =>
  value === 0 ? 'бесплатно' : `${new Intl.NumberFormat('ru-RU').format(Math.round(value))} ₸`;

/** Цену за месяц показываем только там, где она что-то объясняет. */
const toCard = (tariff: Tariff): TariffCard => {
  const price = Number(tariff.price);
  const months = tariff.duration_days / 30;

  return {
    id: tariff.id,
    name: tariff.name,
    duration: pluralDays(tariff.duration_days),
    price: formatPrice(price),
    pricePerMonth:
      price > 0 && tariff.duration_days >= 60 ? `${formatPrice(price / months)} / мес` : undefined,
    recommended: tariff.is_default,
    trial: tariff.is_trial,
  };
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

export const BillingPage: React.FC = () => {
  const { partner } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [tariffs, setTariffs] = useState<TariffCard[]>([]);

  // Условия тарифов живут на бэке и неизменяемы — читаем, а не дублируем.
  useEffect(() => {
    referencesApiService
      .getTariffs()
      .then((list) => setTariffs(list.map(toCard)))
      .catch(() => setTariffs([]));
  }, []);
  const sub = partner?.subscription;

  // Партнёр не активирован — показываем минимальный экран (карточка запуска
  // уже висит глобально на всех страницах).
  if (sub?.is_activated === false) {
    return (
      <div className="m3-page">
        <section className="m3-hero">
          <div className="m3-hero-head">
            <div>
              <h1 className="m3-hero-title">{t.cabinet.noPaymentNeeded}</h1>
              <p className="m3-hero-sub">
                {t.cabinet.noPaymentNeededDesc}
              </p>
            </div>
            <button type="button" className="m3-btn-outlined" onClick={() => navigate('/')}>
              {t.cabinet.toMain}
            </button>
          </div>
        </section>
      </div>
    );
  }

  const statusTone: 'primary' | 'warm' | 'error' | 'tertiary' =
    !sub
      ? 'primary'
      : !sub.is_active
      ? 'error'
      : sub.is_on_trial
      ? (sub.days_left ?? 0) <= 7
        ? 'warm'
        : 'tertiary'
      : (sub.days_left ?? 99) <= 3
      ? 'warm'
      : 'primary';

  const statusLabel = !sub
    ? 'Подписка'
    : !sub.is_active
    ? 'Подписка истекла'
    : sub.is_on_trial
    ? 'Пробный период'
    : 'Подписка активна';

  const daysText = (() => {
    if (!sub) return '';
    const d = sub.days_left ?? 0;
    if (!sub.is_active) {
      const abs = Math.abs(d);
      return abs === 0 ? 'Истекла сегодня' : `Истекла ${abs} дн. назад`;
    }
    if (d === 0) return 'Заканчивается сегодня';
    if (d === 1) return 'Остался 1 день';
    return `Осталось ${d} дн.`;
  })();

  return (
    <div className="m3-page">
      <section className="m3-hero" style={{ marginBottom: 24 }}>
        <div className="m3-hero-head">
          <div>
            <h1 className="m3-hero-title">Подписка и оплата</h1>
            <p className="m3-hero-sub">
              Здесь скоро появится онлайн-оплата тарифов. Пока — свяжитесь с менеджером, он активирует подписку в течение дня.
            </p>
          </div>
        </div>
      </section>

      {sub && (
        <section className={`m3-stat m3-stat--${statusTone}`} style={{ padding: 24, marginBottom: 24, minHeight: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <p className="m3-stat-label" style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Текущий статус
              </p>
              <p className="m3-stat-value" style={{ fontSize: 26, marginTop: 8 }}>
                {statusLabel}
              </p>
              <p style={{ margin: '4px 0 0', fontSize: 14, opacity: 0.85 }}>
                {daysText} · до {formatDate(sub.expires_at)}
              </p>
            </div>
            {sub.trial_started_at && (
              <div style={{ textAlign: 'right' }}>
                <p className="m3-stat-label" style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  Триал начат
                </p>
                <p style={{ margin: '8px 0 0', fontSize: 15, fontWeight: 600 }}>
                  {formatDate(sub.trial_started_at)}
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      <div className="m3-section-head" style={{ marginTop: 8 }}>
        <h2 className="m3-section-title" style={{ fontSize: 22 }}>Тарифные планы</h2>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: 16,
          marginBottom: 32,
        }}
      >
        {tariffs.map((t) => (
          <div
            key={t.id}
            style={{
              position: 'relative',
              padding: 24,
              borderRadius: 'var(--md-shape-xl)',
              background: t.recommended ? 'var(--md-primary-container)' : 'var(--md-surface-lowest)',
              color: t.recommended ? 'var(--md-on-primary-container)' : 'var(--md-on-surface)',
              border: t.recommended ? '1px solid transparent' : '1px solid var(--md-outline-variant)',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              minHeight: 200,
            }}
          >
            {t.recommended && (
              <span
                style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: 'var(--md-primary)',
                  color: 'var(--md-on-primary)',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                Выгодно
              </span>
            )}
            {t.trial && (
              <span
                style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: 'var(--md-tertiary-container)',
                  color: 'var(--md-on-tertiary-container)',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                Бесплатно
              </span>
            )}
            <div>
              <p style={{ margin: 0, fontSize: 12, opacity: 0.7, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600 }}>
                {t.duration}
              </p>
              <h3 style={{ margin: '6px 0 0', fontSize: 22, fontWeight: 500, letterSpacing: '-0.01em' }}>
                {t.name}
              </h3>
            </div>
            <div style={{ marginTop: 'auto' }}>
              <p style={{ margin: 0, fontSize: 32, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1 }}>
                {t.price}
              </p>
              {t.pricePerMonth && (
                <p style={{ margin: '4px 0 0', fontSize: 13, opacity: 0.75 }}>{t.pricePerMonth}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      <section
        style={{
          padding: 32,
          borderRadius: 'var(--md-shape-xl)',
          background:
            'radial-gradient(80% 100% at 100% 0%, color-mix(in srgb, var(--md-primary) 20%, transparent), transparent 70%), var(--md-surface-container)',
          border: '1px solid var(--md-outline-variant)',
        }}
      >
        <div style={{ display: 'grid', gap: 24, gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr)', alignItems: 'start' }}>
          <div>
            <p
              style={{
                margin: 0,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--md-primary)',
              }}
            >
              Поддержка партнёров
            </p>
            <h2 style={{ margin: '10px 0 12px', fontSize: 28, fontWeight: 500, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
              Помогаем оплатить, продлить и разобраться со счётом.
            </h2>
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55, color: 'var(--md-on-surface-variant)', maxWidth: 52 + 'ch' }}>
              Онлайн-оплата в разработке. Напишите или позвоните менеджеру — поможем выбрать тариф, выставим счёт на юрлицо или приём оплаты Kaspi.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 24 }}>
              <a
                href={`tel:${SUPPORT_PHONE}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                  height: 52,
                  padding: '0 24px',
                  borderRadius: 'var(--md-shape-full)',
                  background: 'var(--md-primary)',
                  color: 'var(--md-on-primary)',
                  fontWeight: 600,
                  fontSize: 15,
                  textDecoration: 'none',
                  boxShadow: 'var(--md-elevation-1)',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M3 5a2 2 0 012-2h2.5a1 1 0 01.96.73l1.02 3.56a1 1 0 01-.27.98L7.5 9.75a12 12 0 006.75 6.75l1.48-1.71a1 1 0 01.98-.27l3.56 1.02a1 1 0 01.73.96V19a2 2 0 01-2 2h-1C10.82 21 3 13.18 3 4V5z"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinejoin="round"
                  />
                </svg>
                Позвонить · {SUPPORT_PHONE_DISPLAY}
              </a>
              <a
                href={`https://wa.me/${SUPPORT_PHONE.replace('+', '')}`}
                target="_blank"
                rel="noreferrer"
                className="m3-btn-tonal"
                style={{ height: 52, padding: '0 24px', fontSize: 15, textDecoration: 'none' }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.695.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.42 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
                WhatsApp
              </a>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="m3-btn-text"
                style={{ height: 52, padding: '0 16px', fontSize: 15, textDecoration: 'none' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.75" />
                  <path d="M3 7l9 7 9-7" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
                </svg>
                {SUPPORT_EMAIL}
              </a>
            </div>
          </div>

          <div
            style={{
              padding: 20,
              borderRadius: 'var(--md-shape-lg)',
              background: 'var(--md-surface-lowest)',
              border: '1px solid var(--md-outline-variant)',
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
            }}
          >
            <InfoRow
              icon={
                <svg viewBox="0 0 24 24" fill="none" aria-hidden>
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
                  <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                </svg>
              }
              label="График работы"
              value={SUPPORT_HOURS}
            />
            <InfoRow
              icon={
                <svg viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M8 7l4 4 4-4M4 17V7a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2z"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinejoin="round"
                  />
                </svg>
              }
              label="Оплата"
              value="Kaspi, карта, безнал"
            />
            <InfoRow
              icon={
                <svg viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M9 12l2 2 4-4m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinejoin="round"
                  />
                </svg>
              }
              label="Активация"
              value="в течение рабочего дня"
            />
            <InfoRow
              icon={
                <svg viewBox="0 0 24 24" fill="none" aria-hidden>
                  <rect x="4" y="6" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.75" />
                  <path d="M8 10h8M8 14h5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                </svg>
              }
              label="Закрывающие документы"
              value="акт, счёт-фактура по запросу"
            />
          </div>
        </div>
      </section>

      <p style={{ margin: '24px 0 0', textAlign: 'center', fontSize: 13, color: 'var(--md-on-surface-variant)' }}>
        Скоро здесь появится оплата картой и автопродление.
      </p>
    </div>
  );
};

const InfoRow: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: 'var(--md-shape-sm)',
        background: 'var(--md-primary-container)',
        color: 'var(--md-on-primary-container)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <span style={{ width: 20, height: 20, display: 'inline-flex' }}>{icon}</span>
    </div>
    <div style={{ minWidth: 0 }}>
      <p style={{ margin: 0, fontSize: 12, color: 'var(--md-on-surface-variant)', fontWeight: 500 }}>{label}</p>
      <p style={{ margin: '2px 0 0', fontSize: 14, fontWeight: 500, color: 'var(--md-on-surface)' }}>{value}</p>
    </div>
  </div>
);
